import ConfirmAction from '@/components/confirm-action';
import MeterBar from '@/components/console/meter-bar';
import StatusPill, { type Tone } from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatNumber, humanize } from '@/lib/format';
import { admin_account_path, app_path, console_company_path } from '@/rails/routes';
import type { Account, MeterRow, Plan, ProvisioningStatus } from '@/types/platform';
import type { SharedCompany } from '@/types/shared';
import { Link, router, usePage } from '@inertiajs/react';
import { useState } from 'react';

interface AccountApp {
  id: string;
  name: string;
  slug: string;
  provisioningStatus: ProvisioningStatus;
  lastError: string | null;
  published: boolean;
}

interface AccountMember {
  id: string;
  role: string;
  name: string;
  emailAddress: string;
  staff: boolean;
}

interface Props {
  account: Account;
  apps: AccountApp[];
  members: AccountMember[];
  meter: MeterRow[];
  plans: Plan[];
  isMember: boolean;
}

const SOURCE_LABELS: Record<Account['source'], string> = {
  caprock_client: 'Client job',
  public: 'Public signup',
};

const PROVISIONING_TONES: Record<ProvisioningStatus, Tone> = {
  pending: 'neutral',
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
  halted: 'danger',
};

const Show = ({ account, apps, members, meter, plans, isMember }: Props) => {
  const { currentCompany } = usePage<{ currentCompany?: SharedCompany | null }>().props;
  const [planKey, setPlanKey] = useState(account.plan.key);
  const selectedPlan = plans.find((plan) => plan.key === planKey);
  // The console opens one company at a time, so an app link only resolves for the open one.
  const consoleIsHere = currentCompany?.id === account.id;
  const canOpenApps = isMember && consoleIsHere;

  return (
    <div className="space-y-6">
      <PageHeader
        title={account.name}
        badges={
          <>
            <StatusPill>{SOURCE_LABELS[account.source]}</StatusPill>
            <StatusPill tone="info">{account.plan.name}</StatusPill>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>Account</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Source</dt>
              <dd>{SOURCE_LABELS[account.source]}</dd>
              <dt className="text-muted-foreground">Plan</dt>
              <dd>{account.plan.name}</dd>
              <dt className="text-muted-foreground">Billing</dt>
              <dd>{billingLabel(account)}</dd>
              <dt className="text-muted-foreground">Apps</dt>
              <dd className="tabular-nums">{formatNumber(account.appCount)}</dd>
              <dt className="text-muted-foreground">Members</dt>
              <dd className="tabular-nums">{formatNumber(account.memberCount)}</dd>
              <dt className="text-muted-foreground">Created</dt>
              <dd>{formatDate(account.createdAt)}</dd>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <h2>Usage</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {meter.map((row) => (
              <MeterBar key={row.key} row={row} />
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Plan</h2>
          </CardTitle>
          <CardDescription>
            Attaches the plan with no checkout: a comped or separately invoiced account.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-2">
            <Label htmlFor="account-plan">Plan</Label>
            <Select value={planKey} onValueChange={setPlanKey}>
              <SelectTrigger id="account-plan" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {plans.map((plan) => (
                  <SelectItem key={plan.key} value={plan.key}>
                    {plan.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ConfirmAction
            title={`Put ${account.name} on ${selectedPlan?.name ?? planKey}?`}
            description="Attaches the plan with no checkout."
            confirmLabel="Attach plan"
            onConfirm={() => router.patch(admin_account_path(account.id), { plan_key: planKey })}
          >
            <Button disabled={planKey === account.plan.key}>Attach plan</Button>
          </ConfirmAction>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Members</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <EmptyState description="Nobody is on this account." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">
                      <span className="mr-2">{member.name}</span>
                      {member.staff ? <StatusPill tone="info">Staff</StatusPill> : null}
                    </TableCell>
                    <TableCell>{member.emailAddress}</TableCell>
                    <TableCell>{humanize(member.role)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Apps</h2>
          </CardTitle>
          {isMember ? null : (
            <CardDescription>
              Staff open a client&apos;s apps by being a member of the company. Public accounts are
              not opened.
            </CardDescription>
          )}
          {isMember && !consoleIsHere ? (
            <CardDescription>
              Your console is showing {currentCompany?.name ?? 'another company'}. Switch it to this
              account to open its apps.
            </CardDescription>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {isMember && !consoleIsHere ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.patch(console_company_path(), { company_id: account.id })}
            >
              Switch the console to {account.name}
            </Button>
          ) : null}

          {apps.length === 0 ? (
            <EmptyState description="This account has no apps yet." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>App</TableHead>
                  <TableHead>Setup</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>Last error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      {canOpenApps ? (
                        <Link
                          href={app_path(app.id)}
                          className="font-medium underline-offset-4 hover:underline"
                        >
                          {app.name}
                        </Link>
                      ) : (
                        <span className="font-medium">{app.name}</span>
                      )}
                      <div className="text-muted-foreground text-xs">{app.slug}</div>
                    </TableCell>
                    <TableCell>
                      <StatusPill tone={PROVISIONING_TONES[app.provisioningStatus]}>
                        {humanize(app.provisioningStatus)}
                      </StatusPill>
                    </TableCell>
                    <TableCell>{app.published ? 'Yes' : 'No'}</TableCell>
                    <TableCell className="max-w-xs min-w-48 whitespace-normal">
                      {app.lastError ? (
                        <span className="break-words text-red-700">{app.lastError}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function billingLabel(account: Account): string {
  if (account.comped) return 'Comped';
  if (account.subscriptionStatus) return humanize(account.subscriptionStatus);

  return '—';
}

export default Show;
